import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../ui/dialog';
import { 
  User, Lock, Shield, Smartphone, Eye, EyeOff, 
  CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService, AdminProfile, UpdateProfileRequest, ChangePasswordRequest } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  isAuthenticated: boolean;
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

interface ProfilePageProps {
  user?: User;
  onUserUpdate?: (updatedUser: User) => void;
}

export function ProfilePage({ onUserUpdate }: ProfilePageProps) {
  const [user, setUser] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // API call states
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load profile data
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = await apiService.getProfile();
      setUser(profile);
      setProfileForm({ name: profile.name });
      
      // Convert AdminProfile to User format for parent component
      const userUpdate: User = {
        id: profile.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        isAuthenticated: true,
        twoFactorEnabled: profile.twoFactorEnabled,
        lastLogin: profile.lastLoginAt?.toString(),
        createdAt: profile.createdAt?.toString()
      };
      onUserUpdate?.(userUpdate);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error(extractErrorMessage(error, 'Profil konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileForm.name.trim()) {
      toast.error('Name darf nicht leer sein.');
      return;
    }

    try {
      setUpdatingProfile(true);
      const updateRequest: UpdateProfileRequest = {
        name: profileForm.name.trim(),
      };

      const updatedProfile = await apiService.updateProfile(updateRequest);
      setUser(updatedProfile);
      setIsEditingProfile(false);
      
      // Convert AdminProfile to User format for parent component
      const userUpdate: User = {
        id: updatedProfile.id,
        email: updatedProfile.email,
        name: updatedProfile.name,
        role: updatedProfile.role,
        isAuthenticated: true,
        twoFactorEnabled: updatedProfile.twoFactorEnabled,
        lastLogin: updatedProfile.lastLoginAt?.toString(),
        createdAt: updatedProfile.createdAt?.toString()
      };
      onUserUpdate?.(userUpdate);
      toast.success('Profil aktualisiert.');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(extractErrorMessage(error, 'Profil konnte nicht aktualisiert werden.'));
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein.');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    try {
      setChangingPassword(true);
      const changeRequest: ChangePasswordRequest = {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      };

      await apiService.changePassword(changeRequest);
      
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      toast.success('Passwort geändert.');
    } catch (error: any) {
      console.error('Failed to change password:', error);
      toast.error(extractErrorMessage(error, 'Passwort konnte nicht geändert werden.'));
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load profile data. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">Manage your account settings and security options</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>Profile Information</span>
            </CardTitle>
            <CardDescription>
              Your personal information and account details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isEditingProfile ? (
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <p className="text-sm font-medium">{user.name || 'Not specified'}</p>
                </div>
                <div>
                  <Label>Email Address</Label>
                  <p className="text-sm font-medium">{user.email}</p>
                </div>
                <div>
                  <Label>Account ID</Label>
                  <p className="text-sm font-mono text-gray-600">{user.id}</p>
                </div>
                <div>
                  <Label>Role</Label>
                  <Badge variant="outline">{user.role}</Badge>
                </div>
                {user.createdAt && (
                  <div>
                    <Label>Registered Since</Label>
                    <p className="text-sm text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
                {user.lastLoginAt && (
                  <div>
                    <Label>Last Login</Label>
                    <p className="text-sm text-gray-600">
                      {new Date(user.lastLoginAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
                <Button onClick={() => setIsEditingProfile(true)}>
                  Edit Profile
                </Button>
              </div>
            ) : (
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    required
                    disabled={updatingProfile}
                  />
                </div>
                <div className="flex space-x-2">
                  <Button type="submit" disabled={updatingProfile}>
                    {updatingProfile && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileForm({ name: user?.name || '' });
                    }}
                    disabled={updatingProfile}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Security Settings</span>
            </CardTitle>
            <CardDescription>
              Manage password and 2-Factor Authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Password</Label>
                <Dialog open={isChangingPassword} onOpenChange={setIsChangingPassword}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Change Password</DialogTitle>
                      <DialogDescription>
                        Enter your current password and the new password.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <Label htmlFor="currentPassword">Current Password *</Label>
                        <div className="relative">
                          <Input
                            id="currentPassword"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            required
                            disabled={changingPassword}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="newPassword">New Password *</Label>
                        <div className="relative">
                          <Input
                            id="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            required
                            minLength={8}
                            disabled={changingPassword}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="confirmPassword">Confirm New Password *</Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            required
                            disabled={changingPassword}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setIsChangingPassword(false)}
                          disabled={changingPassword}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" disabled={changingPassword}>
                          {changingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Change Password
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <p className="text-sm text-gray-600">Secure your account with a strong password</p>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label className="flex items-center space-x-2">
                  <Smartphone className="w-4 h-4" />
                  <span>Zwei-Faktor-Authentifizierung</span>
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Die Einrichtung findest du im Konto-Menü oben rechts unter „Zwei-Faktor-Authentifizierung".
                </p>
              </div>
              {user?.twoFactorEnabled ? (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Aktiv
                </Badge>
              ) : (
                <Badge variant="outline">Nicht aktiv</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}